import React from 'react';
import { useImmerReducer } from 'use-immer';
import {
	Flex,
	Box,
	HStack,
	Text,
	Input,
	Textarea,
	Switch,
	NumberInput,
	Button,
	Accordion,
	Heading,
	Separator,
	NativeSelect,
	Field,
	Portal,
	Dialog,
	CloseButton
} from '@chakra-ui/react';
import slugify from 'slugify';
import moment from 'moment';
import SignaturePad from '../components/SignaturePad';
import Autocomplete from '../components/Autocomplete';
import theme from '../theme';

const getCrewMembers = ({ employee_crew_members = [], non_employee_crew_members = [], crew_members = [] }) =>
	[...employee_crew_members, ...non_employee_crew_members, ...crew_members]
		.filter(
			cm =>
				cm.employee_crew_member_name ||
				cm.non_employee_crew_member_name ||
				cm.crew_member_name ||
				cm.employee_crew_member_role ||
				cm.non_employee_crew_member_role ||
				cm.crew_member_role
		)
		.map(cm => ({
			name: cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name,
			role: cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role
		}));

const isValidTimeEntry = value => {
	if (isNaN(Number(value))) {
		return false;
	}

	const index = String(value).length - 1;
	switch (index) {
		case 0:
			return value[index] <= 2;
		case 1:
			return value[index - 1] === 2 ? value[index] <= 4 : true;
		case 2:
			return value[index] <= 5 && value <= 240;
		case 3:
			return value <= 2400;
		default:
			return false;
	}
};

const reducer = (draft, action) => {
	switch (action.type) {
		case 'update_field_value': {
			const { logId, section, field, value } = action.payload;

			if (section) {
				draft.dive_logs[logId].data[section][field] = value;

				if (section === 'times') {
					draft.dive_logs[logId].data.diver_signature = null;
				}
			} else {
				draft.dive_logs[logId].data[field] = value;
			}

			break;
		}
		case 'update_total_dive_times': {
			const { logId, totalBottomTime, totalDiveTime } = action.payload;
			draft.dive_logs[logId].data.times.total_bottom_time = totalBottomTime;
			draft.dive_logs[logId].data.times.total_time_of_dive = totalDiveTime;

			break;
		}
		case 'create_dive_log': {
			const { diver, supervisorName, tenderName, standbyDiverName } = action.payload;
			const log = {
				data: {
					surface_interval_from_previous_dive: '',
					standby_diver_helmet_id: '',
					group: 'N/A',
					repetitive_group: 'N/A',
					supervisor: supervisorName ?? '',
					surface_supplied_air: false,
					max_depth: '',
					diver: diver,
					diver_bailout_bottle: '',
					standby_diver: standbyDiverName,
					standby_diver_bailout_bottle: '',
					tender: tenderName ?? '',
					helmet_used: '',
					max_bottom_time: '',
					visibility: '',
					current: '',
					air_temp: '',
					water_temp: '',
					bottom_condition: '',
					type_of_dive: '',
					residual_nitrogen_time: '',
					diver_company_name: '',
					times: {
						leave_surface: '',
						reach_bottom: '',
						leave_bottom: '',
						reach_surface: '',
						total_bottom_time: '',
						total_time_of_dive: ''
					},
					table_schedule: '',
					thermal_protection_used: '',
					tasks_completed: '',
					divers_condition_upon_surfacing_ok: false,
					divers_condition_details: '',
					drawings: [],
					diver_signature: null,
					supervisor_signature: null
				}
			};

			log.slug = slugify(diver, {
				replacement: '_',
				lower: true,
				remove: /[*+~.()'"!:@]/g
			});

			const diversLogs = Object.values(draft.dive_logs).filter(({ slug }) => slug === log.slug);

			log.diver = diver;
			log.title = `${diver} | Dive - ${diversLogs.length + 1}`;
			log.id = slugify(log.title, {
				replacement: '_',
				lower: true,
				remove: /[*+~.()'"!:@]/g
			});

			draft.dive_logs[log.id] = log;

			break;
		}
		case 'update_diver_name': {
			const { oldId, slug, diver, title, id } = action.payload;

			draft.dive_logs[id] = {
				data: {
					...draft.dive_logs[oldId].data,
					diver_signature: null,
					diver
				},
				slug,
				diver,
				title,
				id
			};

			delete draft.dive_logs[oldId];

			break;
		}
		case 'remove_dive_log': {
			const { logId } = action.payload;
			delete draft.dive_logs[logId];

			break;
		}
		case 'save_signature': {
			const { name, signatureData, logId } = action.payload;
			draft.dive_logs[logId].data[`${name}_signature`] = signatureData;

			break;
		}
		case 'save_drawing': {
			const { drawingData, logId } = action.payload;
			draft.dive_logs[logId].data.drawings.push(drawingData);

			break;
		}
		case 'delete_drawing': {
			const { logId, index } = action.payload;
			draft.dive_logs[logId].data.drawings.splice(index, 1);

			break;
		}
		default:
			throw new Error();
	}
};

export default ({
	formData,
	saveChanges,
	saveForm,
	handoffData,
	edit,
	currentPacket,
	isSubmitted,
	currentForm,
	setDiver,
	activeLog,
	setActiveLog
}) => {
	const [state, dispatch] = useImmerReducer(reducer, { dive_logs: Object.values(formData).length ? formData : {} });

	const [crewMembers, setCrewMembers] = React.useState([]);
	const [selectedDiver, setSelectedDiver] = React.useState('');
	const [createDiveLogName, setCreateDiveLogName] = React.useState('');
	const [cancelDiveDialogOpen, setCancelDiveDialogOpen] = React.useState(false);
	const [editDiverName, setEditDiverName] = React.useState({ show: false, name: '' });
	const [helmetIds, setHelmetIds] = React.useState([]);

	const cancelDiveRef = React.useRef(null);

	const supervisor = crewMembers?.find(({ name, role }) => role === 'field_supervisor' && name !== selectedDiver);
	const tender = crewMembers?.find(({ name, role }) => role === 'tender' && name !== selectedDiver);
	const standbyDiver = crewMembers?.find(({ name, role }) => role === 'standby_diver' && name !== selectedDiver);

	React.useEffect(() => {
		setCrewMembers(getCrewMembers(handoffData));

		const diveLogs = Object.values(state.dive_logs).map(({ id }) => id);

		if (diveLogs.length) {
			setActiveLog(diveLogs[0]);
		}

		setHelmetIds(
			() =>
				JSON.parse(
					currentPacket.packet_forms.find(({ form_id }) => form_id === 'pre_dive_equipment_safety_list')
						?.form_data || {}
				)?.dive_helmets?.helmets?.map(id => ({ id: id })) || []
		);
	}, []);

	React.useEffect(() => {
		if (activeLog) {
			setDiver(state.dive_logs[activeLog].diver);

			let { times, residual_nitrogen_time } = state.dive_logs[activeLog].data;
			let { leave_surface, leave_bottom, reach_surface } = times;

			residual_nitrogen_time = isNaN(parseInt(residual_nitrogen_time)) ? 0 : parseInt(residual_nitrogen_time);
			leave_surface = moment(leave_surface, 'HHmm');
			leave_bottom = moment(leave_bottom, 'HHmm');
			reach_surface = moment(reach_surface, 'HHmm');

			leave_bottom = leave_bottom < leave_surface ? leave_bottom.add(1, 'd') : leave_bottom;
			reach_surface = reach_surface < leave_surface ? reach_surface.add(1, 'd') : reach_surface;

			// 60000 ms in 1 min
			const minToMs = 60000;

			const totalBottomTime = (leave_bottom - leave_surface + residual_nitrogen_time * minToMs) / minToMs;
			const totalDiveTime = (reach_surface - leave_surface) / minToMs;

			dispatch({
				type: 'update_total_dive_times',
				payload: { totalBottomTime, totalDiveTime, logId: activeLog }
			});
		}
	}, [
		activeLog,
		state.dive_logs?.[activeLog]?.data?.residual_nitrogen_time,
		state.dive_logs?.[activeLog]?.data?.times?.leave_surface,
		state.dive_logs?.[activeLog]?.data?.times?.leave_bottom,
		state.dive_logs?.[activeLog]?.data?.times?.reach_surface
	]);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.dive_logs
		});
	}, [state.dive_logs]);

	const hasDiverMatch = React.useMemo(
		() =>
			activeLog &&
			crewMembers?.some(
				({ name = '' }) =>
					activeLog.indexOf(
						slugify(name || '', {
							replacement: '_',
							lower: true,
							remove: /[*+~.()'"!:@]/g
						})
					) !== -1
			),
		[activeLog, crewMembers]
	);

	// default activeLogDiverTotalTimeOfDive to 0 if we're removing the (activeLog) diver
	const saveSignature = (action, activeLogDiverTotalTimeOfDive = 0) => {
		const diveLogs = currentPacket.packet_forms.find(({ form_id }) => form_id === 'dive_log');
		const dfrForm = currentPacket.packet_forms.find(({ form_id }) => form_id === 'daily_field_record');

		// If we're in edit mode, grab the Daily Field Record from the queued changes in case
		// if we've re-assigned a dive log which modifies the original assignee's Daily Field Record which
		// is queued and not available in currentPacket if it's originally modified from this logic
		const dfrFormData =
			saveChanges.find(({ data }) => data.form_id === 'daily_field_record')?.data?.form_data ??
			JSON.parse(dfrForm.form_data);

		// Get total time of dive across all dive logs for diver minus the activeLog
		let totalTimeOfDive = Object.values(JSON.parse(diveLogs.form_data))
			.filter(({ diver, id }) => id !== activeLog && diver === state.dive_logs[activeLog].diver)
			.map(
				({
					data: {
						times: { total_time_of_dive }
					}
				}) => total_time_of_dive || 0
			)
			.reduce((acc, time) => acc + time, 0);

		dfrFormData.crew_members.forEach(crewMember => {
			if (
				crewMember.name === state.dive_logs[activeLog].diver &&
				Object.prototype.hasOwnProperty.call(crewMember.timelogs, '110-1')
			) {
				totalTimeOfDive = Math.round((totalTimeOfDive + activeLogDiverTotalTimeOfDive) / 30) * 30;

				// If Diving, Floating, and Wading is an Onsite Item from DFR
				if (crewMember.timelogs['110-1'].onsite) {
					// Onsite Main item
					const onSiteMainItem = dfrFormData.time_items.find(({ main }) => main === true);

					if (onSiteMainItem) {
						// If there's any Diving, Floating, and Wading hours
						if (crewMember.timelogs['110-1'].hours > 0) {
							// Onsite Main Hours + Previous Dive Time
							const originalHours =
								crewMember.timelogs[onSiteMainItem.key].hours + crewMember.timelogs['110-1'].hours;

							// Total Time of Dive subtracted from combination of Onsite Main hours + Previous Dive Time hours
							crewMember.timelogs[onSiteMainItem.key].hours = originalHours - totalTimeOfDive;
						} else {
							// Otherwise set Onsite Main to Subtracting Total Time of Dive hours from itself
							crewMember.timelogs[onSiteMainItem.key].hours -= totalTimeOfDive;
						}
					}
				}

				crewMember.timelogs['110-1'].hours = totalTimeOfDive;
			}
		});

		saveForm({
			...dfrForm,
			form_data: dfrFormData
		});

		dispatch(action);
	};

	return (
		<Flex direction="column">
			<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
				Create New Dive Log
			</Heading>
			<Flex mb="8px" align="flex-end">
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Select Diver
					</Text>
					<NativeSelect.Root size="sm">
						<NativeSelect.Field
							fontSize={16}
							bgColor={theme.colors.white}
							disabled={isSubmitted && !edit}
							placeholder="Select a Diver..."
							borderRadius="base"
							value={selectedDiver || ''}
							onChange={({ target }) => setSelectedDiver(target.value)}
						>
							{[
								...(crewMembers
									?.map(({ name = '' }) => ({
										label: name,
										value: name
									}))
									.filter(({ value }) => value) ?? []),
								{ label: 'Add Diver', value: 'add_diver' }
							].map(({ label, value }, index) => (
								<option key={index} value={value}>
									{label}
								</option>
							))}
						</NativeSelect.Field>
						<NativeSelect.Indicator />
					</NativeSelect.Root>
				</Box>
				<Button
					size="lg"
					m="8px"
					disabled={
						(isSubmitted && !edit) ||
						selectedDiver === '' ||
						(selectedDiver === 'add_diver' && createDiveLogName.length < 3)
					}
					onClick={() => {
						if (selectedDiver) {
							const diver = selectedDiver === 'add_diver' ? createDiveLogName.trim() : selectedDiver;
							const slug = slugify(diver, {
								replacement: '_',
								lower: true,
								remove: /[*+~.()'"!:@]/g
							});

							const diversLogs = Object.values(state.dive_logs).filter(log => log.slug === slug);

							dispatch({
								type: 'create_dive_log',
								payload: {
									diver,
									supervisorName: supervisor?.name,
									tenderName: tender?.name,
									standbyDiverName: standbyDiver?.name
								}
							});

							setActiveLog(
								slugify(`${diver} | Dive - ${diversLogs.length + 1}`, {
									replacement: '_',
									lower: true,
									remove: /[*+~.()'"!:@]/g
								})
							);
						}
					}}
				>
					Create Dive Log
				</Button>
			</Flex>
			{selectedDiver === 'add_diver' && (
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Diver Name
					</Text>
					<Input
						fontSize={16}
						bgColor={theme.colors.white}
						value={createDiveLogName}
						onChange={({ target }) => setCreateDiveLogName(target.value)}
						flex={1}
					/>
				</Box>
			)}
			<Separator />
			<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
				Existing Dive Logs
			</Heading>
			{state.dive_logs ? (
				<Flex mb="8px" align="flex-end">
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text fontSize={16} as="label" pb="4px">
							Dive Logs
						</Text>
						<NativeSelect.Root size="sm">
							<NativeSelect.Field
								fontSize={16}
								bgColor={theme.colors.white}
								placeholder=" "
								w="100%"
								borderRadius="base"
								value={activeLog || ''}
								onChange={({ target }) => setActiveLog(target.value)}
							>
								{Object.entries(state.dive_logs).map(([value, { title }], index) => (
									<option key={index} value={value}>
										{title}
									</option>
								))}
							</NativeSelect.Field>
							<NativeSelect.Indicator />
						</NativeSelect.Root>
					</Box>
					{activeLog ? (
						<>
							<Button
								size="lg"
								minW="135px"
								margin="8px"
								disabled={isSubmitted && !edit}
								bg={theme.colors.blue[700]}
								_hover={{ background: theme.colors.blue[800] }}
								color={theme.colors.white}
								onClick={() => {
									editDiverName.show
										? setEditDiverName({ show: false, name: '' })
										: setEditDiverName({ show: true, name: state.dive_logs[activeLog].diver });
								}}
							>
								Edit Name
							</Button>
							<Button
								size="lg"
								minW="135px"
								m="8px"
								disabled={isSubmitted && !edit}
								bg={theme.colors.red[500]}
								_hover={{ background: theme.colors.red[600] }}
								color={theme.colors.white}
								onClick={() => setCancelDiveDialogOpen(true)}
							>
								Cancel Dive
							</Button>
							<Dialog.Root
								role="alertdialog"
								initialFocusEl={cancelDiveRef}
								onOpenChange={({ open }) => {
									if (!open) {
										setCancelDiveDialogOpen(false);
									}
								}}
								open={cancelDiveDialogOpen}
								placement="center"
							>
								<Portal>
									<Dialog.Backdrop />
									<Dialog.Positioner>
										<Dialog.Content opacity="1 !important">
											<Dialog.Header>
												<Dialog.Title fontSize="lg" fontWeight="bold">
													Confirm Delete
												</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<p>Are you sure you want to cancel this dive?</p>
											</Dialog.Body>
											<Dialog.Footer>
												<Dialog.ActionTrigger asChild>
													<Button
														size="lg"
														ref={cancelDiveRef}
														onClick={() => setCancelDiveDialogOpen(false)}
													>
														No
													</Button>
												</Dialog.ActionTrigger>
												<Button
													colorPalette="red"
													size="lg"
													ml={3}
													bg={theme.colors.red[500]}
													_hover={{ background: theme.colors.red[600] }}
													color={theme.colors.white}
													onClick={() => {
														saveSignature({
															type: 'remove_dive_log',
															payload: { logId: activeLog }
														});

														setCancelDiveDialogOpen(false);
														setSelectedDiver('');
														setActiveLog('');
													}}
												>
													Yes
												</Button>
											</Dialog.Footer>
											<Dialog.CloseTrigger asChild>
												<CloseButton size="sm" />
											</Dialog.CloseTrigger>
										</Dialog.Content>
									</Dialog.Positioner>
								</Portal>
							</Dialog.Root>
						</>
					) : null}
				</Flex>
			) : null}
			{editDiverName.show ? (
				<Flex mb="8px" align="flex-end">
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text fontSize={16} as="label" pb="4px">
							Diver Name
						</Text>
						<Autocomplete
							disabled={isSubmitted && !edit}
							dataList={crewMembers}
							fieldName="name"
							value={editDiverName.name}
							onChange={value => setEditDiverName(prev => ({ ...prev, name: value }))}
							onSelected={value => setEditDiverName(prev => ({ ...prev, name: value }))}
						/>
					</Box>
					<Button
						size="lg"
						minW="135px"
						margin="8px"
						disabled={isSubmitted && !edit}
						bg={theme.colors.blue[700]}
						_hover={{ background: theme.colors.blue[800] }}
						color={theme.colors.white}
						onClick={() => {
							if (editDiverName.name !== state.dive_logs[activeLog].diver) {
								const updatedSlug = slugify(editDiverName.name, {
									replacement: '_',
									lower: true,
									remove: /[*+~.()'"!:@]/g
								});
								const diversLogs = Object.values(state.dive_logs).filter(
									({ slug }) => slug === updatedSlug
								);
								const updatedTitle = `${editDiverName.name} | Dive - ${diversLogs.length + 1}`;
								const updatedId = slugify(updatedTitle, {
									replacement: '_',
									lower: true,
									remove: /[*+~.()'"!:@]/g
								});

								dispatch({
									type: 'update_diver_name',
									payload: {
										oldId: activeLog,
										diver: editDiverName.name,
										slug: updatedSlug,
										title: updatedTitle,
										id: updatedId
									}
								});

								saveSignature({
									type: 'remove_dive_log',
									payload: { logId: activeLog }
								});

								setEditDiverName({ show: false, name: '' });
								setActiveLog(updatedId);
							} else {
								setEditDiverName(prevDiverName => ({ ...prevDiverName, show: false }));
							}
						}}
					>
						Confirm
					</Button>
					<Button
						size="lg"
						minW="135px"
						m="8px"
						disabled={isSubmitted && !edit}
						bg={theme.colors.red[500]}
						_hover={{ background: theme.colors.red[600] }}
						color={theme.colors.white}
						onClick={() => setEditDiverName({ show: false, name: '' })}
					>
						Cancel
					</Button>
				</Flex>
			) : null}
			{activeLog ? (
				<Flex direction="column" mt={4}>
					<Box flex="1" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text fontSize={16} as="label" pb="4px">
							Supervisor
						</Text>
						<Field.Root invalid={!state.dive_logs[activeLog].data.supervisor}>
							<NativeSelect.Root size="sm">
								<NativeSelect.Field
									fontSize={16}
									bgColor={theme.colors.white}
									placeholder=" "
									w="100%"
									value={state.dive_logs[activeLog].data.supervisor || ''}
									borderRadius="base"
									onChange={({ target }) =>
										dispatch({
											type: 'update_field_value',
											payload: {
												logId: activeLog,
												field: 'supervisor',
												value: target.value
											}
										})
									}
									disabled={isSubmitted && !edit}
									_disabled={{
										opacity: 1,
										bgColor: theme.colors.gray[400],
										color: theme.colors.black
									}}
								>
									{crewMembers
										?.filter(({ name = null }) => name !== state.dive_logs[activeLog].diver)
										.map(({ name }, index) => (
											<option key={index} value={name}>
												{name}
											</option>
										))}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Field.Root>
					</Box>
					<Flex>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Diver Bailout Bottle (PSI)
							</Text>
							<Field.Root invalid={!state.dive_logs[activeLog].data.diver_bailout_bottle}>
								<NumberInput.Root
									w="100%"
									value={state.dive_logs[activeLog].data.diver_bailout_bottle}
									disabled={isSubmitted && !edit}
									onValueChange={e =>
										dispatch({
											type: 'update_field_value',
											payload: {
												logId: activeLog,
												field: 'diver_bailout_bottle',
												value: e.value
											}
										})
									}
								>
									<NumberInput.Input
										fontSize={16}
										bgColor={theme.colors.white}
										borderRadius="base"
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</NumberInput.Root>
							</Field.Root>
						</Box>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Diver Helmet ID#
							</Text>
							<Autocomplete
								disabled={isSubmitted && !edit}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
								dataList={helmetIds}
								fieldName="id"
								value={state.dive_logs[activeLog].data.helmet_used}
								onChange={value =>
									dispatch({
										type: 'update_field_value',
										payload: {
											logId: activeLog,
											field: 'helmet_used',
											value
										}
									})
								}
								onSelected={value =>
									dispatch({
										type: 'update_field_value',
										payload: {
											logId: activeLog,
											field: 'helmet_used',
											value
										}
									})
								}
							/>
						</Box>
					</Flex>
					<Flex>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Standby Diver
							</Text>
							<Field.Root invalid={!state.dive_logs[activeLog].data.standby_diver}>
								<NativeSelect.Root size="sm">
									<NativeSelect.Field
										fontSize={16}
										bgColor={theme.colors.white}
										placeholder=" "
										borderRadius="base"
										value={state.dive_logs[activeLog].data.standby_diver || ''}
										onChange={({ target }) =>
											dispatch({
												type: 'update_field_value',
												payload: {
													logId: activeLog,
													field: 'standby_diver',
													value: target.value
												}
											})
										}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									>
										{crewMembers
											?.filter(({ name = null }) => name !== state.dive_logs[activeLog].diver)
											.map(({ name }, index) => (
												<option key={index} value={name}>
													{name}
												</option>
											))}
									</NativeSelect.Field>
									<NativeSelect.Indicator />
								</NativeSelect.Root>
							</Field.Root>
						</Box>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Standby Diver Bailout Bottle (PSI)
							</Text>
							<Field.Root invalid={!state.dive_logs[activeLog].data.standby_diver_bailout_bottle}>
								<NumberInput.Root
									w="100%"
									value={state.dive_logs[activeLog].data.standby_diver_bailout_bottle}
									disabled={isSubmitted && !edit}
									onValueChange={e =>
										dispatch({
											type: 'update_field_value',
											payload: {
												logId: activeLog,
												field: 'standby_diver_bailout_bottle',
												value: e.value
											}
										})
									}
								>
									<NumberInput.Input
										fontSize={16}
										bgColor={theme.colors.white}
										borderRadius="base"
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</NumberInput.Root>
							</Field.Root>
						</Box>
					</Flex>
					<Flex>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Standby Diver Helmet ID#
							</Text>
							<Field.Root invalid={!state.dive_logs[activeLog].data.standby_diver_helmet_id}>
								<Autocomplete
									disabled={isSubmitted && !edit}
									_disabled={{
										opacity: 1,
										bgColor: theme.colors.gray[400],
										color: theme.colors.black
									}}
									dataList={helmetIds}
									fieldName="id"
									value={state.dive_logs[activeLog].data.standby_diver_helmet_id}
									onChange={value =>
										dispatch({
											type: 'update_field_value',
											payload: {
												logId: activeLog,
												field: 'standby_diver_helmet_id',
												value
											}
										})
									}
									onSelected={value =>
										dispatch({
											type: 'update_field_value',
											payload: {
												logId: activeLog,
												field: 'standby_diver_helmet_id',
												value
											}
										})
									}
								/>
							</Field.Root>
						</Box>
						<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Tender
							</Text>
							<Field.Root invalid={!state.dive_logs[activeLog].data.tender}>
								<NativeSelect.Root size="sm">
									<NativeSelect.Field
										fontSize={16}
										bgColor={theme.colors.white}
										placeholder=" "
										borderRadius="base"
										value={state.dive_logs[activeLog].data.tender || ''}
										onChange={({ target }) =>
											dispatch({
												type: 'update_field_value',
												payload: {
													logId: activeLog,
													field: 'tender',
													value: target.value
												}
											})
										}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									>
										{crewMembers.map(({ name }, index) => (
											<option key={index} value={name}>
												{name}
											</option>
										))}
									</NativeSelect.Field>
									<NativeSelect.Indicator />
								</NativeSelect.Root>
							</Field.Root>
						</Box>
					</Flex>
					<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3]}>
						<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
							<Accordion.ItemTrigger>
								<Accordion.ItemIndicator />
								<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
									Before Dive
								</Text>
							</Accordion.ItemTrigger>
							<Accordion.ItemContent p={0}>
								<Accordion.ItemBody>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Surface Interval from Previous Dive (HRS:MINS)
											</Text>
											<Field.Root
												invalid={
													state.dive_logs[activeLog].data.surface_interval_from_previous_dive
														.length > 0 &&
													state.dive_logs[activeLog].data.surface_interval_from_previous_dive
														.length < 4
												}
											>
												<Input
													value={
														state.dive_logs[activeLog].data
															.surface_interval_from_previous_dive.length > 1
															? `${state.dive_logs[
																	activeLog
																].data.surface_interval_from_previous_dive.slice(
																	0,
																	2
																)}:${state.dive_logs[
																	activeLog
																].data.surface_interval_from_previous_dive.slice(2)}`
															: state.dive_logs[activeLog].data
																	.surface_interval_from_previous_dive
													}
													disabled={isSubmitted && !edit}
													onChange={({ nativeEvent: { inputType }, target }) => {
														// TODO: Test this
														let value;

														if (inputType === 'deleteContentBackward') {
															value = state.dive_logs[
																activeLog
															].data.surface_interval_from_previous_dive.slice(
																0,
																state.dive_logs[activeLog].data
																	.surface_interval_from_previous_dive.length - 1
															);
														} else {
															value = target.value.replace(/\D/g, '');
														}

														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'surface_interval_from_previous_dive',
																value:
																	isNaN(value) || value.length > 4
																		? state.dive_logs[activeLog].data
																				.surface_interval_from_previous_dive
																		: value
															}
														});
													}}
													fontSize={16}
													bgColor={theme.colors.white}
													borderRadius="base"
													placeholder="--:--"
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Repetitive Group
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.repetitive_group}>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														bgColor={theme.colors.white}
														placeholder=" "
														borderRadius="base"
														value={state.dive_logs[activeLog].data.repetitive_group || ''}
														onChange={({ target }) =>
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	field: 'repetitive_group',
																	value: target.value
																}
															})
														}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{[
															'N/A',
															...new Array(26)
																.fill(1)
																.map((_, i) => String.fromCharCode(65 + i))
														].map((group, index) => (
															<option key={index} value={group}>
																{group}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Field.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Residual Nitrogen Time (mins)
											</Text>
											<Field.Root
												invalid={!state.dive_logs[activeLog].data.residual_nitrogen_time}
											>
												<NumberInput.Root
													w="100%"
													value={state.dive_logs[activeLog].data.residual_nitrogen_time}
													disabled={isSubmitted && !edit}
													onValueChange={e =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'residual_nitrogen_time',
																value: e.value
															}
														})
													}
												>
													<NumberInput.Input
														fontSize={16}
														bgColor={theme.colors.white}
														borderRadius="base"
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</NumberInput.Root>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Type of Dive
											</Text>
											{/* NOTE: Type of dive will need to be updated both here and on the API */}
											{/* in RenderPacketController.php under the render_packet module for any updates */}
											{/* for proper value indexing */}
											<Field.Root invalid={!state.dive_logs[activeLog].data.type_of_dive}>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														bgColor={theme.colors.white}
														placeholder=" "
														borderRadius="base"
														value={state.dive_logs[activeLog].data.type_of_dive || ''}
														onChange={({ target }) =>
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	field: 'type_of_dive',
																	value: target.value
																}
															})
														}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{[
															{
																label: 'Surface Supplied Air',
																value: 'surface_supplied_air'
															},
															{ label: 'SCUBA', value: 'scuba' },
															{ label: 'Wading', value: 'wading' }
														].map(({ label, value }, index) => (
															<option key={index} value={value}>
																{label}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Field.Root>
										</Box>
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
						<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
							<Accordion.ItemTrigger>
								<Accordion.ItemIndicator />
								<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
									Conditions
								</Text>
							</Accordion.ItemTrigger>
							<Accordion.ItemContent p={0}>
								<Accordion.ItemBody>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Visibility (ft)
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.visibility}>
												<NumberInput.Root
													w="100%"
													value={state.dive_logs[activeLog].data.visibility}
													disabled={isSubmitted && !edit}
													onValueChange={e =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'visibility',
																value: e.value
															}
														})
													}
												>
													<NumberInput.Input
														fontSize={16}
														bgColor={theme.colors.white}
														borderRadius="base"
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</NumberInput.Root>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Current (KT)
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.current}>
												<NumberInput.Root
													w="100%"
													value={state.dive_logs[activeLog].data.current}
													disabled={isSubmitted && !edit}
													onValueChange={e =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'current',
																value: e.value
															}
														})
													}
												>
													<NumberInput.Input
														fontSize={16}
														bgColor={theme.colors.white}
														borderRadius="base"
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</NumberInput.Root>
											</Field.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Air Temp (°F)
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.air_temp}>
												<NumberInput.Root
													w="100%"
													value={state.dive_logs[activeLog].data.air_temp}
													disabled={isSubmitted && !edit}
													onValueChange={e =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'air_temp',
																value: e.value
															}
														})
													}
												>
													<NumberInput.Input
														fontSize={16}
														bgColor={theme.colors.white}
														borderRadius="base"
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</NumberInput.Root>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Water Temp (°F)
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.water_temp}>
												<NumberInput.Root
													w="100%"
													value={state.dive_logs[activeLog].data.water_temp}
													disabled={isSubmitted && !edit}
													onValueChange={e =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'water_temp',
																value: e.value
															}
														})
													}
												>
													<NumberInput.Input
														fontSize={16}
														bgColor={theme.colors.white}
														borderRadius="base"
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</NumberInput.Root>
											</Field.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Bottom Condition
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.bottom_condition}>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={state.dive_logs[activeLog].data.bottom_condition}
													onChange={({ target }) =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'bottom_condition',
																value: target.value
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Thermal Protection Used
											</Text>
											<Field.Root
												invalid={!state.dive_logs[activeLog].data.thermal_protection_used}
											>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={state.dive_logs[activeLog].data.thermal_protection_used}
													onChange={({ target }) =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'thermal_protection_used',
																value: target.value
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
						<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
							<Accordion.ItemTrigger>
								<Accordion.ItemIndicator />
								<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
									Dive Time
								</Text>
							</Accordion.ItemTrigger>
							<Accordion.ItemContent p={0}>
								<Accordion.ItemBody>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Leave Surface (LS)
											</Text>
											<Field.Root
												invalid={
													!state.dive_logs[activeLog].data.times.leave_surface ||
													state.dive_logs[activeLog].data.times.leave_surface.length < 4 ||
													state.dive_logs[activeLog].data.times.leave_surface > 2400
												}
											>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													borderRadius="base"
													placeholder="----"
													value={state.dive_logs[activeLog].data.times.leave_surface}
													onChange={({ target }) => {
														if (!target.value || isValidTimeEntry(target.value)) {
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	section: 'times',
																	field: 'leave_surface',
																	value: target.value
																}
															});
														}
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Reach Bottom (RB)
											</Text>
											<Field.Root
												invalid={
													!state.dive_logs[activeLog].data.times.reach_bottom ||
													state.dive_logs[activeLog].data.times.reach_bottom.length < 4 ||
													state.dive_logs[activeLog].data.times.reach_bottom > 2400
												}
											>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													borderRadius="base"
													placeholder="----"
													value={state.dive_logs[activeLog].data.times.reach_bottom}
													onChange={({ target }) => {
														if (!target.value || isValidTimeEntry(target.value)) {
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	section: 'times',
																	field: 'reach_bottom',
																	value: target.value
																}
															});
														}
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Leave Bottom (LB)
											</Text>
											<Field.Root
												invalid={
													!state.dive_logs[activeLog].data.times.leave_bottom ||
													state.dive_logs[activeLog].data.times.leave_bottom.length < 4 ||
													state.dive_logs[activeLog].data.times.leave_bottom > 2400
												}
											>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													borderRadius="base"
													placeholder="----"
													value={state.dive_logs[activeLog].data.times.leave_bottom}
													onChange={({ target }) => {
														if (!target.value || isValidTimeEntry(target.value)) {
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	section: 'times',
																	field: 'leave_bottom',
																	value: target.value
																}
															});
														}
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Reach Surface (RS)
											</Text>
											<Field.Root
												invalid={
													!state.dive_logs[activeLog].data.times.reach_surface ||
													state.dive_logs[activeLog].data.times.reach_surface.length < 4
												}
											>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													borderRadius="base"
													placeholder="----"
													value={state.dive_logs[activeLog].data.times.reach_surface}
													onChange={({ target }) => {
														if (!target.value || isValidTimeEntry(target.value)) {
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	section: 'times',
																	field: 'reach_surface',
																	value: target.value
																}
															});
														}
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Total Bottom Time (TBT - includes RNT)
											</Text>
											<Flex h="2.5rem" alignItems="center">
												<Text fontSize={16}>
													{parseInt(
														state.dive_logs[activeLog].data.times.total_bottom_time
													) >= 0
														? `${parseInt(
																state.dive_logs[activeLog].data.times.total_bottom_time
															)} mins`
														: '0 mins'}
												</Text>
											</Flex>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Total Time of Dive (TTD)
											</Text>
											<Flex h="2.5rem" alignItems="center">
												<Text fontSize={16}>
													{parseInt(
														state.dive_logs[activeLog].data.times.total_time_of_dive
													) >= 0
														? `${parseInt(
																state.dive_logs[activeLog].data.times.total_time_of_dive
															)} mins`
														: '0 mins'}
												</Text>
											</Flex>
										</Box>
									</Flex>
									<Box flex="1" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Max Depth (ft)
										</Text>
										<Field.Root invalid={!state.dive_logs[activeLog].data.max_depth}>
											<Input
												fontSize={16}
												bgColor={theme.colors.white}
												borderRadius="base"
												value={state.dive_logs[activeLog].data.max_depth}
												onChange={({ target }) =>
													dispatch({
														type: 'update_field_value',
														payload: {
															logId: activeLog,
															field: 'max_depth',
															value:
																target.value.length > 4
																	? state.dive_logs[activeLog].data.max_depth
																	: target.value
														}
													})
												}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Field.Root>
									</Box>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Table/Schedule
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.table_schedule}>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={state.dive_logs[activeLog].data.table_schedule}
													onChange={({ target }) =>
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'table_schedule',
																value: target.value
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Field.Root>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Repetitive Group
											</Text>
											<Field.Root invalid={!state.dive_logs[activeLog].data.group}>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														bgColor={theme.colors.white}
														placeholder=" "
														borderRadius="base"
														value={state.dive_logs[activeLog].data.group || ''}
														onChange={({ target }) =>
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	field: 'group',
																	value: target.value
																}
															})
														}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{[
															'N/A',
															...new Array(26)
																.fill(1)
																.map((_, i) => String.fromCharCode(65 + i))
														].map((group, index) => (
															<option key={index} value={group}>
																{group}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Field.Root>
										</Box>
									</Flex>
									<Box
										flex="1"
										mx="8px"
										mt="8px"
										mb="28px"
										p="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
									>
										<Text fontSize={16} as="label" pb="4px">
											Max Allowable Bottom Time (MINS)
										</Text>
										<Field.Root invalid={!state.dive_logs[activeLog].data.max_bottom_time}>
											<Input
												fontSize={16}
												bgColor={theme.colors.white}
												borderRadius="base"
												value={state.dive_logs[activeLog].data.max_bottom_time}
												onChange={({ target }) =>
													dispatch({
														type: 'update_field_value',
														payload: {
															logId: activeLog,
															field: 'max_bottom_time',
															value:
																target.value.length > 4
																	? state.dive_logs[activeLog].data.max_bottom_time
																	: target.value
														}
													})
												}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Field.Root>
									</Box>
									<Box flex="1" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Tasks Completed
										</Text>
										<Field.Root invalid={!state.dive_logs[activeLog].data.tasks_completed}>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.dive_logs[activeLog].data.tasks_completed}
												onChange={({ target }) =>
													dispatch({
														type: 'update_field_value',
														payload: {
															logId: activeLog,
															field: 'tasks_completed',
															value: target.value
														}
													})
												}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Field.Root>
									</Box>
									<Box flex="1" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Diver Condition Details
										</Text>
										<Field.Root
											invalid={
												!(
													state.dive_logs[activeLog].data
														.divers_condition_upon_surfacing_ok ||
													state.dive_logs[activeLog].data.divers_condition_details
												)
											}
										>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.dive_logs[activeLog].data.divers_condition_details}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															logId: activeLog,
															field: 'divers_condition_details',
															value: target.value
														}
													});

													if (!target.value) {
														dispatch({
															type: 'update_field_value',
															payload: {
																logId: activeLog,
																field: 'divers_condition_upon_surfacing_ok',
																value: false
															}
														});
													}
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Field.Root>
									</Box>
									<HStack
										flex="1"
										p="8px"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
									>
										<Switch.Root
											colorPalette="blue"
											size="lg"
											onCheckedChange={() =>
												dispatch({
													type: 'update_field_value',
													payload: {
														logId: activeLog,
														field: 'divers_condition_upon_surfacing_ok',
														value: !state.dive_logs[activeLog].data
															.divers_condition_upon_surfacing_ok
													}
												})
											}
											checked={state.dive_logs[activeLog].data.divers_condition_upon_surfacing_ok}
											disabled={
												!state.dive_logs[activeLog].data.divers_condition_details ||
												(isSubmitted && !edit)
											}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<Text fontSize={16} as="label" pl="4px">
											Divers Condition Upon Surfacing OK
										</Text>
									</HStack>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
						<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
							<Accordion.ItemTrigger>
								<Accordion.ItemIndicator />
								<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
									Signatures
								</Text>
							</Accordion.ItemTrigger>
							<Accordion.ItemContent p={0}>
								<Accordion.ItemBody>
									<Flex>
										<Flex direction="column" w="100%" p="8px">
											<Box w="100%" py="8px">
												<Text fontSize={16} as="label" pb="4px">
													Diver Name
												</Text>
												<Field.Root invalid={!state.dive_logs[activeLog].data.diver}>
													<Input
														fontSize={16}
														bgColor={theme.colors.gray[500]}
														value={state.dive_logs[activeLog].data.diver}
														disabled={true}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</Field.Root>
											</Box>
											{!hasDiverMatch && (
												<Box w="100%" py="8px">
													<Text fontSize={16} as="label" pb="4px">
														Company Name
													</Text>
													<Input
														fontSize={16}
														bgColor={theme.colors.gray[500]}
														onChange={({ target }) =>
															dispatch({
																type: 'update_field_value',
																payload: {
																	logId: activeLog,
																	field: 'diver_company_name',
																	value: target.value
																}
															})
														}
														value={state.dive_logs[activeLog].data.diver_company_name}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</Box>
											)}
											<Box
												w="100%"
												py="8px"
												borderBottomWidth="2px"
												borderBottomColor={
													state.dive_logs[activeLog].data.diver_signature
														? 'transparent'
														: 'red'
												}
											>
												<SignaturePad
													disabled={isSubmitted && !edit}
													src={
														state.dive_logs[activeLog].data.diver_signature ||
														'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
													}
													onSign={signatureData =>
														saveSignature(
															{
																type: 'save_signature',
																payload: {
																	name: 'diver',
																	signatureData,
																	logId: activeLog
																}
															},
															state.dive_logs[activeLog].data.times.total_time_of_dive
														)
													}
												/>
											</Box>
										</Flex>
										<Flex direction="column" w="100%" p="8px">
											<Box w="100%" py="8px">
												<Text fontSize={16} as="label" pb="4px">
													Supervisor Name
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.gray[500]}
													value={state.dive_logs[activeLog].data.supervisor}
													disabled={true}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
											<Box
												w="100%"
												py="8px"
												borderBottomWidth="2px"
												borderBottomColor={
													state.dive_logs[activeLog].data.supervisor_signature
														? 'transparent'
														: 'red'
												}
											>
												<SignaturePad
													disabled={isSubmitted && !edit}
													src={
														state.dive_logs[activeLog].data.supervisor_signature ||
														'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
													}
													onSign={signatureData =>
														dispatch({
															type: 'save_signature',
															payload: {
																name: 'supervisor',
																signatureData,
																logId: activeLog
															}
														})
													}
												/>
											</Box>
										</Flex>
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
					</Accordion.Root>
				</Flex>
			) : null}
		</Flex>
	);
};
